import { App as CapacitorApp } from '@capacitor/app';
import { Grid } from '@mui/material';
import { RoutalPalette } from '@nx-smartmonkey/ui';
import { forwardRef, useEffect, useRef } from 'react';
import styled from 'styled-components';

import { SyncErrorIcon } from '../../../assets/svg/offline/SyncErrorIcon';
import { SyncIcon } from '../../../assets/svg/offline/SyncIcon';
import { intl } from '../../../helpers/IntGlobalProvider';
import { Text } from '../../../react-components/Text';
import { LocalNotificationEnum, routalLocalNotifications } from '../../../services/localNotifications';
import { usePendingReports } from '../hooks/pendingReports/usePendingReports';
import { useSyncPendingReports } from '../hooks/pendingReports/useSyncPendingReports';

export const BANNER_HEIGHT = 60;

// --- Spinner Icon Animation ---
const RotatingSyncIcon = styled(SyncIcon)`
  animation: spin 1s linear infinite;
  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

export const PendingReportsBanner = forwardRef<HTMLDivElement, React.PropsWithChildren<unknown>>((props) => {
  const { data: pendingReports } = usePendingReports();
  const { isSyncing, syncAllPending } = useSyncPendingReports();

  const pauseListenerRef = useRef<{ remove: () => void } | null>(null);

  const reportsRef = useRef(pendingReports);

  // Keep ref in sync with the latest pendingReports
  useEffect(() => {
    reportsRef.current = pendingReports;
  }, [pendingReports]);

  useEffect(() => {
    const setupPauseListener = async () => {
      if (pauseListenerRef.current?.remove) {
        await pauseListenerRef.current.remove();
      }
      const newListener = await CapacitorApp.addListener(`pause`, () => {
        if (reportsRef.current?.length > 0) {
          const title = intl.formatMessage({ id: `drivers.notification.local_reports.title` });
          const body = intl.formatMessage({ id: `drivers.notification.local_reports.body` });

          routalLocalNotifications.scheduleNotification({
            title,
            body,
            group: `PendingReports`,
            sound: `none`,
            actionTypeId: LocalNotificationEnum.PENDING_REPORTS,
            id: 69,
          });
        }
      });

      pauseListenerRef.current = newListener;
    };

    setupPauseListener();

    return () => {
      pauseListenerRef.current?.remove?.();
    };
  }, []);

  const showBanner = pendingReports && pendingReports.length > 0;

  return (
    <>
      {showBanner && (
        <Grid
          container
          direction="row"
          gap="12px"
          wrap="nowrap"
          alignItems="center"
          style={{
            width: `100%`,
            padding: `12px`,
            height: BANNER_HEIGHT,
            backgroundColor: RoutalPalette.warning.light,
            cursor: isSyncing ? `default` : `pointer`,
          }}
          onClick={() => {
            if (!isSyncing) {
              syncAllPending();
            }
          }}
        >
          {isSyncing ? <RotatingSyncIcon /> : <SyncErrorIcon />}

          <Text
            variant="small"
            style={{ color: RoutalPalette.secondary60 }}
            intlId="drivers.syncing_reports"
            intlValues={{ value: pendingReports.length }}
          />
        </Grid>
      )}
      <div
        style={{
          position: `relative`,
          height: `calc(100% - ${showBanner ? BANNER_HEIGHT : 0}px)`, // Adjust height to account for the banner
        }}
      >
        {props.children}
      </div>
    </>
  );
});
